// ==========================================================
// mefExpediente — cliente y lectura del expediente SIAF del MEF
// ==========================================================
// La Consulta de Expediente Administrativo del MEF no manda cabeceras CORS,
// amarra el captcha a una cookie de sesión y además solo atiende conexiones
// domésticas peruanas (descarta las de los servidores en la nube). Por eso la
// ida y vuelta pasa por el puente que corre en el equipo del usuario:
// `servidor-mef/server.js` (ver MEF_EXPEDIENTE.md).
//
// Aquí vive todo lo demás: el parseo del HTML que devuelve el portal y el
// armado del modelo que la pantalla muestra (cadena del pago, totales por
// fase, pagos con su detracción). El portal entrega una bitácora plana de
// movimientos SIAF; el trabajo de esta capa es volverla legible:
//
//   movimientos (filas crudas)
//     └─ registros   = filas agrupadas por fase + secuencia + correlativo,
//                      con su estado vigente (la última fila por fecha)
//         └─ pagos   = cada devengado con los giros que lo cubren
//             └─ detracción SUNAT cuando el giro se parte en dos
//
// Nada de esto se guarda en Supabase: el historial y la última consulta se
// quedan en el localStorage del equipo, con claves `nuvia:pagos:`. Lo único que
// sí pasa por la base es el cupo diario de consultas por plan, y eso vive
// aparte, en `cuotaPagos.js`.


const CLAVE_HISTORIAL = 'nuvia:pagos:historial';
const CLAVE_SNAPSHOT = 'nuvia:pagos:exp:';
const MAX_HISTORIAL = 8;

// ----------------------------------------------------------
// Catálogos del SIAF
// ----------------------------------------------------------
export const CICLOS = {
  G: 'Gasto',
  I: 'Ingreso',
};

// Fases del ciclo de gasto, en el orden en que ocurren.
export const FASES = {
  C: { clave: 'compromiso', nombre: 'Compromiso', orden: 1 },
  D: { clave: 'devengado', nombre: 'Devengado', orden: 2 },
  G: { clave: 'girado', nombre: 'Girado', orden: 3 },
  P: { clave: 'pagado', nombre: 'Pagado', orden: 4 },
};

// Estados que publica el portal. A (aprobado), F (en firma) y V (verificación)
// son los que se ven en los expedientes; el resto son los códigos habituales
// del SIAF y, si aparece uno que no está acá, se muestra el código tal cual.
export const ESTADOS = {
  A: { nombre: 'Aprobado', tono: 'ok' },
  V: { nombre: 'En verificación', tono: 'proceso' },
  F: { nombre: 'Pendiente de firma', tono: 'espera' },
  P: { nombre: 'Pagado', tono: 'ok' },
  T: { nombre: 'Transmitido', tono: 'proceso' },
  E: { nombre: 'Enviado', tono: 'proceso' },
  R: { nombre: 'Rechazado', tono: 'alerta' },
  N: { nombre: 'No aprobado', tono: 'alerta' },
  X: { nombre: 'Anulado', tono: 'alerta' },
};

// Documentos que se ven en los expedientes de bienes y servicios. Los que no
// estén acá salen como "Doc NNN": es preferible al riesgo de etiquetar mal.
export const DOCUMENTOS = {
  '031': 'Orden de Compra',
  '032': 'Orden de Servicio',
  '027': 'Recibo por Honorarios',
  '243': 'Nota de Pago',
};

// Tasas de detracción de SUNAT que se usan para reconocer el giro partido.
const TASAS_DETRACCION = [1.5, 2, 3, 4, 5, 8, 10, 12, 15];

export const nombreEstado = (codigo) =>
  ESTADOS[codigo]?.nombre || (codigo ? `Estado ${codigo}` : 'Sin estado');

export const tonoEstado = (codigo) => ESTADOS[codigo]?.tono || 'neutro';

export const nombreDocumento = (codigo) =>
  DOCUMENTOS[codigo] || (codigo ? `Doc ${codigo}` : null);

// ----------------------------------------------------------
// Formato
// ----------------------------------------------------------
export const formatearMonto = (valor, moneda = 'S/') => {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '—';
  return `${moneda} ${n.toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatearPorcentaje = (valor) => {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('es-PE', { maximumFractionDigits: 1 })}%`;
};

/** "14/09/2026 18:47:40" → Date; también acepta solo la fecha. */
export const parsearFechaSiaf = (texto) => {
  if (!texto) return null;
  const m = String(texto).trim().match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/
  );
  if (!m) return null;
  const [, d, mes, a, hh = '0', mm = '0', ss = '0'] = m;
  const fecha = new Date(+a, +mes - 1, +d, +hh, +mm, +ss);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

export const formatearFechaHora = (texto) => {
  const f = parsearFechaSiaf(texto);
  if (!f) return texto || '—';
  return f.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** "36,000.00" → 36000 */
const aMonto = (texto) => {
  const limpio = String(texto || '').replace(/[^\d.-]/g, '');
  if (!limpio) return null;
  const n = Number(limpio);
  return Number.isFinite(n) ? n : null;
};

const limpiar = (texto) => String(texto ?? '').replace(/\s+/g, ' ').trim();

// ----------------------------------------------------------
// Parseo del HTML del portal
// ----------------------------------------------------------
const COLUMNAS = [
  'ciclo',
  'fase',
  'secuencia',
  'correlativo',
  'codDoc',
  'numDoc',
  'fecha',
  'ff',
  'moneda',
  'monto',
  'estado',
  'fechaProceso',
  'idTrx',
];

const valorInput = (doc, id) =>
  limpiar(doc.querySelector(`#${id}`)?.getAttribute('value') || '') || null;

/**
 * Lee la cabecera (año, entidad, expediente, tipo de operación…).
 * Al copiar y pegar desde el navegador, los `<input readonly>` del portal a
 * veces no sobreviven el viaje por el portapapeles; cuando eso pasa la
 * cabecera sale vacía y la pantalla la completa con lo que digitó el usuario.
 */
const leerCabecera = (doc) => ({
  anoEje: valorInput(doc, 'anoEje'),
  secEjec: valorInput(doc, 'secEjec'),
  entidad: valorInput(doc, 'secEjecNombre'),
  expediente: valorInput(doc, 'expediente'),
  tipoOperacion: valorInput(doc, 'tipoOperacion'),
  tipoOperacionNombre: valorInput(doc, 'tipoOperacionNombre'),
  modalidadCompra: valorInput(doc, 'modalidadCompra'),
  modalidadCompraNombre: valorInput(doc, 'modalidadCompraNombre'),
  tipoProceso: valorInput(doc, 'tipoProceso'),
  tipoProcesoNombre: valorInput(doc, 'tipoProcesoNombre'),
});

// Encabezados de la tabla de movimientos, para reconocerla cuando el pegado
// llega sin el id `expedienteDetalles` (Chrome reescribe el HTML al copiar).
const SENAS_TABLA = ['ciclo', 'fase', 'sec', 'corr', 'doc'];

const normalizar = (texto) =>
  limpiar(texto)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** ¿Esta tabla es la de movimientos del expediente? */
const esTablaDeMovimientos = (tabla) => {
  const encabezados = [...tabla.querySelectorAll('tr')]
    .slice(0, 2)
    .flatMap((tr) => [...tr.children].map((c) => normalizar(c.textContent)));
  if (!encabezados.length) return false;
  return SENAS_TABLA.every((sena) => encabezados.some((e) => e === sena || e.startsWith(sena)));
};

/**
 * Ubica la tabla de movimientos: por id cuando viene el HTML del portal, y
 * por sus encabezados cuando viene pegado desde el navegador.
 */
const ubicarTabla = (doc) => {
  const porId = doc.querySelector('#expedienteDetalles');
  if (porId) return porId;
  return [...doc.querySelectorAll('table')].find(esTablaDeMovimientos) || null;
};

/** Convierte una fila de celdas (ya en texto) en un movimiento. */
const armarMovimiento = (celdas) => {
  const mov = {};
  COLUMNAS.forEach((nombre, i) => {
    mov[nombre] = celdas[i] || null;
  });

  return {
    ciclo: mov.ciclo,
    fase: mov.fase,
    secuencia: mov.secuencia,
    correlativo: mov.correlativo,
    codDoc: mov.codDoc,
    documento: nombreDocumento(mov.codDoc),
    numDoc: mov.numDoc,
    fecha: mov.fecha,
    ff: mov.ff,
    moneda: mov.moneda,
    monto: aMonto(mov.monto),
    estado: mov.estado,
    fechaProceso: mov.fechaProceso,
    // El Id Trx viene dentro de un <label>; cuando no hay, la celda va vacía.
    idTrx: mov.idTrx || null,
  };
};

/** ¿La fila tiene pinta de movimiento y no de encabezado o de pie? */
const esFilaDeDatos = (celdas) => {
  if (celdas.length < 10) return false;
  // Ciclo y fase son una sola letra; la secuencia es un número.
  const [ciclo, fase, secuencia] = celdas;
  return /^[A-Za-z]$/.test(ciclo || '') && /^[A-Za-z]$/.test(fase || '') &&
    /^\d+$/.test(secuencia || '');
};

/** Lee las filas de la tabla de movimientos de una página. */
const leerMovimientos = (doc) => {
  const tabla = ubicarTabla(doc);
  if (!tabla) return [];

  const filas = [...tabla.querySelectorAll('tr')];

  return filas
    .map((tr) => [...tr.children].map((c) => limpiar(c.textContent || '')))
    .filter(esFilaDeDatos)
    .map(armarMovimiento);
};

/**
 * Convierte las páginas HTML del portal en cabecera + movimientos. Sirve
 * igual para el HTML que llega pegado desde el navegador.
 */
export const parsearPaginas = (paginas = []) => {
  const parser = new DOMParser();
  let cabecera = null;
  const movimientos = [];

  paginas.forEach((html) => {
    const doc = parser.parseFromString(html, 'text/html');
    const leida = leerCabecera(doc);
    // Se queda con la primera cabecera que traiga algo.
    if (!cabecera || (!cabecera.expediente && leida.expediente)) cabecera = leida;
    movimientos.push(...leerMovimientos(doc));
  });

  return { cabecera: cabecera || {}, movimientos };
};

// ----------------------------------------------------------
// Modelo: de la bitácora plana a algo que se entienda
// ----------------------------------------------------------
/**
 * Agrupa las filas por fase + secuencia + correlativo. Cada grupo es un
 * registro del SIAF, y sus filas son el historial por el que pasó: el estado
 * vigente es el de la fila más reciente que trae estado.
 */
const agruparRegistros = (movimientos) => {
  const mapa = new Map();

  movimientos.forEach((m, indice) => {
    const clave = `${m.ciclo || '-'}|${m.fase || '-'}|${m.secuencia || '-'}|${m.correlativo || '-'}`;
    if (!mapa.has(clave)) {
      mapa.set(clave, {
        clave,
        ciclo: m.ciclo,
        fase: m.fase,
        faseNombre: FASES[m.fase]?.nombre || m.fase,
        secuencia: m.secuencia,
        correlativo: m.correlativo,
        filas: [],
      });
    }
    mapa.get(clave).filas.push({ ...m, indice });
  });

  return [...mapa.values()].map((reg) => {
    const porFecha = [...reg.filas].sort((a, b) => {
      const fa = parsearFechaSiaf(a.fechaProceso);
      const fb = parsearFechaSiaf(b.fechaProceso);
      if (fa && fb) return fa - fb;
      if (fa) return -1;
      if (fb) return 1;
      return a.indice - b.indice;
    });

    const conEstado = porFecha.filter((f) => f.estado);
    const vigente = conEstado[conEstado.length - 1] || porFecha[porFecha.length - 1];
    const conMonto = porFecha.filter((f) => Number.isFinite(f.monto));
    const conDoc = porFecha.find((f) => f.numDoc) || porFecha.find((f) => f.codDoc);

    const fechas = porFecha
      .map((f) => parsearFechaSiaf(f.fechaProceso))
      .filter(Boolean);

    return {
      ...reg,
      filas: porFecha,
      // El monto se repite en las filas del historial: basta el último.
      monto: conMonto.length ? conMonto[conMonto.length - 1].monto : null,
      moneda: conMonto.find((f) => f.moneda)?.moneda || 'S/.',
      estado: vigente?.estado || null,
      estadoNombre: nombreEstado(vigente?.estado),
      tono: tonoEstado(vigente?.estado),
      codDoc: conDoc?.codDoc || null,
      documento: nombreDocumento(conDoc?.codDoc),
      numDoc: conDoc?.numDoc || null,
      fechaDoc: porFecha.find((f) => f.fecha)?.fecha || null,
      ff: porFecha.find((f) => f.ff)?.ff || null,
      idTrx: porFecha.find((f) => f.idTrx)?.idTrx || null,
      ultimaFecha: fechas.length ? fechas[fechas.length - 1] : null,
      ultimaFechaTexto:
        [...porFecha].reverse().find((f) => f.fechaProceso)?.fechaProceso || null,
      aprobado: vigente?.estado === 'A' || vigente?.estado === 'P',
    };
  });
};

const sumar = (registros) =>
  registros.reduce((acc, r) => acc + (Number(r.monto) || 0), 0);

/**
 * Busca el subconjunto de giros (hasta 3) que suma exactamente el devengado.
 * En la práctica un devengado se gira de una vez o se parte en dos por la
 * detracción, así que la búsqueda se mantiene corta a propósito.
 */
const buscarGirosDe = (montoDevengado, disponibles) => {
  const casi = (a, b) => Math.abs(a - b) < 0.01;

  for (let i = 0; i < disponibles.length; i++) {
    if (casi(disponibles[i].monto || 0, montoDevengado)) return [disponibles[i]];
  }
  for (let i = 0; i < disponibles.length; i++) {
    for (let j = i + 1; j < disponibles.length; j++) {
      const total = (disponibles[i].monto || 0) + (disponibles[j].monto || 0);
      if (casi(total, montoDevengado)) return [disponibles[i], disponibles[j]];
    }
  }
  for (let i = 0; i < disponibles.length; i++) {
    for (let j = i + 1; j < disponibles.length; j++) {
      for (let k = j + 1; k < disponibles.length; k++) {
        const total =
          (disponibles[i].monto || 0) +
          (disponibles[j].monto || 0) +
          (disponibles[k].monto || 0);
        if (casi(total, montoDevengado)) {
          return [disponibles[i], disponibles[j], disponibles[k]];
        }
      }
    }
  }
  return [];
};

/** Reconoce el giro de detracción dentro de un pago partido en dos. */
const detectarDetraccion = (montoDevengado, giros) => {
  if (!montoDevengado || giros.length < 2) return null;

  const menor = [...giros].sort((a, b) => (a.monto || 0) - (b.monto || 0))[0];
  if (!menor?.monto) return null;

  const tasa = (menor.monto / montoDevengado) * 100;
  const reconocida = TASAS_DETRACCION.find((t) => Math.abs(t - tasa) < 0.05);
  if (!reconocida) return null;

  return {
    tasa: reconocida,
    monto: menor.monto,
    cuenta: menor.numDoc || null,
    giro: menor,
  };
};

/**
 * Arma los pagos: cada devengado con los giros que lo cubren. Lo que no calza
 * con ningún devengado queda aparte para no esconder movimientos.
 */
const construirPagos = (devengados, girados) => {
  const libres = [...girados];
  const pagos = devengados.map((dev, i) => {
    let giros = [];

    if (devengados.length === 1) {
      // Un solo devengado: todos los giros son suyos.
      giros = libres.splice(0, libres.length);
    } else if (Number.isFinite(dev.monto)) {
      giros = buscarGirosDe(dev.monto, libres);
      giros.forEach((g) => {
        const pos = libres.indexOf(g);
        if (pos >= 0) libres.splice(pos, 1);
      });
    }

    const giradoTotal = sumar(giros);
    const giradoAprobado = sumar(giros.filter((g) => g.aprobado));
    const detraccion = detectarDetraccion(dev.monto, giros);

    return {
      numero: i + 1,
      devengado: dev,
      giros,
      monto: dev.monto,
      giradoTotal,
      giradoAprobado,
      detraccion,
      // Lo que realmente le llega al proveedor cuando hay detracción.
      montoLibre: detraccion ? (dev.monto || 0) - detraccion.monto : dev.monto,
      estado: estadoDePago(dev, giros),
    };
  });

  return { pagos, girosSinVincular: libres };
};

/** Resume en una etiqueta en qué punto quedó un pago. */
function estadoDePago(devengado, giros) {
  if (!giros.length) {
    return devengado.aprobado
      ? { clave: 'por-girar', nombre: 'Pendiente de giro', tono: 'espera' }
      : { clave: 'devengando', nombre: 'Devengado en trámite', tono: 'proceso' };
  }
  if (giros.every((g) => g.estado === 'P')) {
    return { clave: 'pagado', nombre: 'Pagado', tono: 'ok' };
  }
  if (giros.every((g) => g.aprobado)) {
    return { clave: 'girado', nombre: 'Girado', tono: 'ok' };
  }
  return { clave: 'en-tesoreria', nombre: 'Giro en trámite', tono: 'proceso' };
}

/**
 * Cadena del gasto: los cuatro eslabones con su monto y su estado. Es la
 * lectura rápida de "¿en qué parte se quedó mi pago?".
 */
const construirCadena = (porFase, totales) => {
  const paso = (codigo, monto, registros) => {
    const hay = registros.length > 0;
    const todosAprobados = hay && registros.every((r) => r.aprobado);
    // La fecha del eslabón es la más reciente de sus registros, no la del
    // último por número de secuencia.
    const ultima = registros
      .filter((r) => r.ultimaFecha)
      .sort((a, b) => a.ultimaFecha - b.ultimaFecha)
      .map((r) => r.ultimaFechaTexto)
      .pop();

    return {
      codigo,
      clave: FASES[codigo].clave,
      nombre: FASES[codigo].nombre,
      monto,
      registros: registros.length,
      fecha: ultima,
      estado: !hay ? 'pendiente' : todosAprobados ? 'completo' : 'proceso',
    };
  };

  // El portal cierra la bitácora en el girado: la fase P no aparece ni cuando
  // el giro ya está aprobado y el proveedor cobró (se comprobó en los
  // expedientes 9274 y 411). Un cuarto eslabón siempre vacío se leería como un
  // trámite que falta, así que la cadena termina en el girado salvo que el
  // expediente traiga de verdad movimientos de pagado.
  return [
    paso('C', totales.comprometido, porFase.C),
    paso('D', totales.devengado, porFase.D),
    paso('G', totales.girado, porFase.G),
    ...(porFase.P.length ? [paso('P', totales.pagado, porFase.P)] : []),
  ];
};

/** Frase corta que dice dónde está el expediente y qué falta. */
const resumirSituacion = (cadena, totales, pagos) => {
  const girosEnTramite = pagos.filter((p) => p.estado.clave === 'en-tesoreria');
  const porGirar = pagos.filter((p) => p.estado.clave === 'por-girar');

  if (!totales.comprometido) {
    return {
      titulo: 'Sin compromiso registrado',
      detalle: 'El expediente aún no tiene la fase de compromiso aprobada.',
      tono: 'espera',
    };
  }
  if (totales.pagado > 0 && totales.pagado >= totales.devengado) {
    return {
      titulo: 'Pago ejecutado',
      detalle: `El SIAF registra ${formatearMonto(totales.pagado)} pagados.`,
      tono: 'ok',
    };
  }
  if (girosEnTramite.length) {
    return {
      titulo: 'En Tesorería',
      detalle: `${girosEnTramite.length} giro(s) por ${formatearMonto(
        girosEnTramite.reduce((acc, p) => acc + (p.giradoTotal || 0), 0)
      )} ya están registrados y esperan firma o verificación.`,
      tono: 'proceso',
    };
  }
  if (totales.giradoAprobado > 0 && totales.giradoAprobado >= totales.devengado) {
    return {
      titulo: 'Girado',
      detalle: 'El giro está aprobado; el abono depende del banco.',
      tono: 'ok',
    };
  }
  if (porGirar.length) {
    return {
      titulo: 'Devengado, falta el giro',
      detalle: `${formatearMonto(totales.porGirar)} con conformidad aprobada, pendientes de giro en Tesorería.`,
      tono: 'espera',
    };
  }
  if (totales.devengado > 0) {
    return {
      titulo: 'Devengado en trámite',
      detalle: 'La conformidad está registrada pero todavía no queda aprobada.',
      tono: 'proceso',
    };
  }
  return {
    titulo: 'Comprometido',
    detalle: 'Falta registrar la conformidad (devengado) de la entrega.',
    tono: 'espera',
  };
};

/**
 * Punto de entrada del modelo: recibe lo parseado y devuelve todo lo que la
 * pantalla necesita.
 */
export const construirSeguimiento = ({ cabecera = {}, movimientos = [] }) => {
  const registros = agruparRegistros(movimientos);

  const ordenarSec = (a, b) =>
    (Number(a.secuencia) || 0) - (Number(b.secuencia) || 0) ||
    (Number(a.correlativo) || 0) - (Number(b.correlativo) || 0);

  const porFase = { C: [], D: [], G: [], P: [] };
  registros.forEach((r) => {
    if (porFase[r.fase]) porFase[r.fase].push(r);
  });
  Object.values(porFase).forEach((lista) => lista.sort(ordenarSec));

  const otros = registros.filter((r) => !porFase[r.fase]);

  const comprometido = sumar(porFase.C);
  const devengado = sumar(porFase.D);
  const girado = sumar(porFase.G);
  const pagado = sumar(porFase.P);
  const giradoAprobado = sumar(porFase.G.filter((r) => r.aprobado));

  const totales = {
    comprometido,
    devengado,
    girado,
    giradoAprobado,
    giradoEnTramite: girado - giradoAprobado,
    pagado,
    saldoPorDevengar: Math.max(comprometido - devengado, 0),
    porGirar: Math.max(devengado - girado, 0),
    avance: comprometido > 0 ? (giradoAprobado / comprometido) * 100 : 0,
    avanceDevengado: comprometido > 0 ? (devengado / comprometido) * 100 : 0,
    avanceGirado: comprometido > 0 ? (girado / comprometido) * 100 : 0,
  };

  const { pagos, girosSinVincular } = construirPagos(porFase.D, porFase.G);
  const cadena = construirCadena(porFase, totales);
  const situacion = resumirSituacion(cadena, totales, pagos);

  const detraccion = pagos.find((p) => p.detraccion)?.detraccion || null;

  return {
    cabecera: {
      ...cabecera,
      cicloNombre: CICLOS[movimientos[0]?.ciclo] || null,
      fuenteFinanciamiento: registros.find((r) => r.ff)?.ff || null,
    },
    movimientos,
    registros,
    porFase,
    otros,
    totales,
    pagos,
    girosSinVincular,
    cadena,
    situacion,
    detraccion,
  };
};

// ----------------------------------------------------------
// Llamadas a la Edge Function
// ----------------------------------------------------------
// El puente corre en el equipo del usuario (ver servidor-mef/server.js):
// el MEF solo atiende conexiones domésticas peruanas y descarta las de los
// servidores en la nube. En producción esta URL apunta al túnel que publica
// ese mismo puente.
const PUENTE = process.env.REACT_APP_MEF_PUENTE || 'http://localhost:8787';

/**
 * En desarrollo el puente corre en esta misma máquina y el aviso puede decir
 * cómo encenderlo; en producción lo atiende el equipo del portal por un túnel,
 * y al usuario hay que decirle otra cosa.
 */
export const PUENTE_ES_LOCAL = /^https?:\/\/(localhost|127\.0\.0\.1)/.test(PUENTE);

const invocar = async (body) => {
  let r;
  try {
    r = await fetch(PUENTE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // El plan gratuito de ngrok mete una página de advertencia delante del
        // túnel; con esta cabecera la saltea y responde el puente directo. No
        // molesta cuando el túnel es otro (Cloudflare la ignora).
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify(body),
    });
  } catch {
    // De cara al usuario esto es "el servicio no está disponible": que la
    // consulta salga por un puente en un equipo nuestro es asunto interno y
    // no le da nada accionable. En desarrollo sí conviene el detalle técnico.
    const fallo = new Error(
      PUENTE_ES_LOCAL
        ? 'No se pudo contactar al puente del MEF. Revisa que esté encendido (servidor-mef).'
        : 'Seguimiento de Pagos está en mantenimiento momentáneo. Vuelve a intentarlo en unos minutos.'
    );
    fallo.motivo = 'puente';
    throw fallo;
  }

  let data = null;
  try {
    data = await r.json();
  } catch {
    data = null;
  }

  if (!r.ok || data?.error) {
    const fallo = new Error(data?.error || `El servicio respondió ${r.status}.`);
    fallo.motivo = data?.motivo || 'red';
    throw fallo;
  }

  return data;
};

/** Abre una sesión en el MEF y trae la imagen del código. */
export const pedirCaptcha = () => invocar({ accion: 'captcha' });

/**
 * Manda el formulario y devuelve el expediente ya ordenado.
 * @param {{sesion:string, captcha:string, anoEje:string, secEjec:string, expediente:string, refrescar?:boolean}} datos
 */
export const consultarExpediente = async ({
  sesion,
  captcha,
  anoEje,
  secEjec,
  expediente,
  refrescar = false,
}) => {
  const data = await invocar({
    accion: refrescar ? 'refrescar' : 'consultar',
    sesion,
    captcha,
    anoEje,
    secEjec,
    expediente,
  });

  const parseado = parsearPaginas(data?.paginas || []);
  const seguimiento = construirSeguimiento(parseado);

  return {
    ...seguimiento,
    consulta: { anoEje, secEjec, expediente },
    sesion: data?.sesion || sesion,
    captcha: data?.captcha || captcha,
    truncado: Boolean(data?.truncado),
    consultadoEn: Date.now(),
  };
};

// ----------------------------------------------------------
// Historial y última consulta (solo en el equipo del usuario)
// ----------------------------------------------------------
export const claveExpediente = ({ anoEje, secEjec, expediente }) =>
  `${anoEje}-${String(secEjec).padStart(3, '0')}-${expediente}`;

export const leerHistorial = () => {
  try {
    const crudo = localStorage.getItem(CLAVE_HISTORIAL);
    const lista = crudo ? JSON.parse(crudo) : [];
    return Array.isArray(lista) ? lista : [];
  } catch {
    return [];
  }
};

export const guardarEnHistorial = (entrada) => {
  try {
    const clave = claveExpediente(entrada);
    const previo = leerHistorial().filter((e) => claveExpediente(e) !== clave);
    const lista = [{ ...entrada, consultadoEn: Date.now() }, ...previo].slice(
      0,
      MAX_HISTORIAL
    );
    localStorage.setItem(CLAVE_HISTORIAL, JSON.stringify(lista));
    return lista;
  } catch {
    return leerHistorial();
  }
};

export const borrarDelHistorial = (entrada) => {
  try {
    const clave = claveExpediente(entrada);
    const lista = leerHistorial().filter((e) => claveExpediente(e) !== clave);
    localStorage.setItem(CLAVE_HISTORIAL, JSON.stringify(lista));
    return lista;
  } catch {
    return leerHistorial();
  }
};

/**
 * Guarda la última consulta del expediente para poder abrirla sin captcha y
 * para comparar contra la siguiente (qué se movió desde la última vez).
 */
export const guardarSnapshot = (datos) => {
  try {
    const clave = CLAVE_SNAPSHOT + claveExpediente(datos.consulta);
    localStorage.setItem(
      clave,
      JSON.stringify({
        guardadoEn: datos.consultadoEn,
        cabecera: datos.cabecera,
        movimientos: datos.movimientos,
        consulta: datos.consulta,
      })
    );
  } catch {
    // Sin espacio en localStorage: la consulta sigue valiendo.
  }
};

export const leerSnapshot = (consulta) => {
  try {
    const crudo = localStorage.getItem(CLAVE_SNAPSHOT + claveExpediente(consulta));
    if (!crudo) return null;
    const guardado = JSON.parse(crudo);
    const seguimiento = construirSeguimiento(guardado);
    return {
      ...seguimiento,
      consulta: guardado.consulta,
      consultadoEn: guardado.guardadoEn,
      desdeCache: true,
    };
  } catch {
    return null;
  }
};

/** Movimientos nuevos respecto de la consulta anterior del mismo expediente. */
export const compararConSnapshot = (anterior, actual) => {
  if (!anterior?.movimientos?.length) return { nuevos: 0, hay: false };
  const huella = (m) =>
    [m.fase, m.secuencia, m.correlativo, m.estado, m.fechaProceso, m.monto].join('|');
  const previos = new Set(anterior.movimientos.map(huella));
  const nuevos = actual.movimientos.filter((m) => !previos.has(huella(m)));
  return { nuevos: nuevos.length, hay: nuevos.length > 0, detalle: nuevos };
};
